import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '@/shared/auth/store';

/**
 * BigList items 4 + 15 (Chris, 2026-09-04) — **retained search state**.
 *
 * <pre>
 *   #4  "When searching if you zoom, hit an icon and go back all criteria and the results are
 *        gone, is there any way to retain them for x amount of time before making the user
 *        reenter them - if so - do this everywhere it's relevant in PH10"
 *   #15 "...if I hit back and it was a screen that say I scanned a carton, or a screen where I
 *        had search results that we return to that data populated on that last/back screen."
 * </pre>
 *
 * <p><b>Why this exists at all.</b> PowerBuilder's MDI keeps a search sheet ALIVE behind the
 * zoom child: `f_open_window` opens `w_order_header` on top, the operator closes it, and
 * `w_order_search` is still sitting there with its criteria typed and its `dw_1` still holding
 * the retrieved buffer — no retrieve happens, because the sheet never went away. PH10's zoom is
 * a route change: `TableBrowserPage` even remounts deliberately on `key + location.search`
 * (#1421), which is right for a DRILL but wrong for the RETURN. Nothing in the app restored
 * search state before this hook — the A4 audit confirmed it
 * (`docs/audits/biglist/14-guards-back-sort-wording-dropdowns.md` §15, "no sessionStorage /
 * location.state restoration mechanism exists yet ... grep: 0 hits"). So the operator retyped.
 *
 * <p><b>The decision</b> (orchestrator, needs-chris card 125): retain <i>per browser tab</i>,
 * keyed by route, for <b>30 minutes of inactivity</b>; an explicit Clear/Reset wipes it.
 *
 * <h4>Why sessionStorage and not localStorage / Zustand / the URL</h4>
 * <ul>
 *   <li><b>Per tab</b> is the PB sheet's lifetime. Two tabs on the same browse are two sheets in
 *       PB's MDI, each with its own criteria — `localStorage` would cross-wire them, and
 *       `sessionStorage` is per-tab by definition.</li>
 *   <li><b>Survives a real Back.</b> Browser Back is a fresh mount, so component state and
 *       Zustand (in-memory, wiped by a reload) both lose. `sessionStorage` survives in-tab
 *       navigation, Back/Forward AND an F5 in the same tab.</li>
 *   <li><b>Not the URL.</b> Criteria in the query string would change the route, and
 *       `TableBrowserPage` keys its remount on `location.search` — restoring through the URL
 *       would fight the drill isolation the same file depends on.</li>
 *   <li><b>Dies with the tab.</b> Nothing warehouse data-shaped is left on the machine for the
 *       next shift: closing the tab drops every entry.</li>
 * </ul>
 *
 * <h4>What is retained, and what is deliberately NOT</h4>
 * <p>Retained: the operator's committed <b>criteria</b> (whatever the page calls it), optionally
 * the <b>result rows</b> as fetched, and a small <b>view</b> record (selected row + first visible
 * row) so the grid comes back scrolled where it was.
 *
 * <p><b>Never retained: a dirty edit buffer.</b> The unsaved-changes guard (slice-3557) exists to
 * make an operator decide about pending edits at the moment they navigate; resurrecting those
 * edits half an hour later — possibly over rows another user has since changed — would be a data
 * defect wearing a convenience costume. Callers pass committed search state and fetched rows
 * only. There is no code path here that can carry a pending cell edit.
 *
 * <h4>The size guard</h4>
 * <p>`sessionStorage` is ~5 MB per origin and shared by every route in the tab, so a single
 * "ALL" retrieve of a 40-column browse must not be allowed to eat it. Above
 * {@link RETAINED_SEARCH_MAX_RESULT_BYTES} the rows are dropped and the entry is marked
 * {@link RetainedSearch.resultsDropped} — the criteria still come back and the page re-runs the
 * search itself. That is PB-shaped anyway: the criteria are what the operator typed, the rows are
 * what the database said, and re-asking the database is never wrong.
 *
 * <h4>Never on /login, never on a kiosk</h4>
 * <p>A kiosk display is a shared screen in a break room ({@code shared/auth/on401.ts}); leaving
 * one operator's search on it for the next person is a privacy problem, not a feature. The login
 * route has nothing worth retaining and is the one place an unauthenticated stranger types.
 */

/** How long an untouched entry survives. 30 minutes of inactivity (needs-chris card 125). */
export const RETAINED_SEARCH_TTL_MS = 30 * 60_000;

/**
 * Above this many bytes of serialised rows, the rows are dropped and only the criteria kept.
 * ~2 MB against a ~5 MB per-origin `sessionStorage` budget shared by every route in the tab.
 */
export const RETAINED_SEARCH_MAX_RESULT_BYTES = 2_000_000;

/** Key namespace. One flat prefix so a sweep (logout, quota panic) can find every entry. */
export const RETAINED_SEARCH_PREFIX = 'ph:retain:';

/**
 * How long a state change settles before it is written.
 *
 * <p>Not politeness — arithmetic. The Query By band's value input calls `onRowsChange` on every
 * KEYSTROKE, and the band is part of the criteria, so an undebounced write would hand
 * `sessionStorage` a fresh copy of a result set that can be megabytes once per character typed.
 * A quarter-second is invisible to a person and collapses a typed word into one write. Nothing
 * is at risk in the gap: the unmount and `pagehide` writes are immediate and unconditional, and
 * unmount is exactly the moment that matters — the operator drilling away.
 */
export const RETAINED_SEARCH_WRITE_DEBOUNCE_MS = 250;

/** Where the grid was. Restored best-effort — a shorter result set simply lands at the top. */
export interface RetainedView {
  /** Row id of the focused/selected row, in whatever id shape the page already uses. */
  selectedRowId?: string | null;
  /** AG-Grid's first displayed row index, for `ensureIndexVisible`. */
  firstRowIndex?: number | null;
  /** Raw pixel scroll offset, for pages whose results are a plain scroll container. */
  scrollTop?: number | null;
}

/** One retained entry, as it sits in `sessionStorage`. */
export interface RetainedSearch<S, R> {
  /** The page's committed criteria — its own shape, opaque here. */
  state: S;
  /** The rows as fetched, or `null` when they were never offered or were dropped. */
  results: R | null;
  /** True when {@link results} were dropped by the size guard: re-run the search on return. */
  resultsDropped: boolean;
  /** Where the grid was when the operator left. */
  view: RetainedView;
  /** Epoch ms of the last write. Inactivity is measured from here. */
  updatedAt: number;
}

export interface UseRetainedSearchOptions<S, R> {
  /**
   * Stable identity of the screen, including anything that makes it a DIFFERENT screen — the
   * browse key plus its drill query string, a tab id, a pinned-filter signature. Two routes
   * that show different data must never share a key.
   */
  routeKey: string;
  /** The committed criteria to retain. Written whenever it changes and {@link retain} is true. */
  state: S;
  /** The fetched rows, when the page wants them retained too. Omit to retain criteria only. */
  results?: R | null | undefined;
  /**
   * Called at every write (including the unmount write) to sample where the grid is. A getter,
   * not a value, so scrolling costs nothing: nothing is written while the operator scrolls, and
   * the final position is captured when they leave.
   */
  getView?: (() => RetainedView | null) | undefined;
  /**
   * The write gate — "has the operator actually searched here?". False keeps ambient defaults
   * on 576 browse screens out of storage. Default true.
   */
  retain?: boolean | undefined;
  /**
   * Bumped once per explicit Search. Re-arms writing after a Clear. Same token the
   * `useCriteriaCollapse` latch uses — pass the same value.
   */
  searchToken?: string | number | null | undefined;
  /**
   * Bumped once per explicit Clear/Reset. Wipes the entry and suppresses further writes until
   * {@link searchToken} changes, so the Clear survives the re-render that follows it.
   */
  clearToken?: string | number | null | undefined;
  /** Master switch. False = read nothing, write nothing. Default true. */
  enabled?: boolean | undefined;
  /**
   * An entry the caller already read with {@link readRetainedSearch}, used verbatim instead of
   * reading again. `null` means "I looked, there was nothing"; only `undefined` (the default)
   * makes the hook read for itself.
   *
   * <p>`TableBrowserPage` needs this: its `criteria`/`searchFired`/`queryRows` `useState`
   * initialisers sit ~5,500 lines ABOVE the point where the fetched rows exist, so it reads the
   * entry at the top of the component and hands the same object down here — one `JSON.parse` of
   * a result set that can be megabytes, not two.
   */
  initialEntry?: RetainedSearch<S, R> | null | undefined;
  /** Override the inactivity window. Tests only, in practice. */
  ttlMs?: number | undefined;
}

export interface UseRetainedSearchResult<S, R> {
  /**
   * What was found on the FIRST render, or null. Frozen for the life of the mount so a
   * `useState` initialiser and a later effect see the same thing.
   */
  restored: RetainedSearch<S, R> | null;
  /** Convenience: `restored?.resultsDropped === true`. The page should re-run its search. */
  resultsDropped: boolean;
  /** Wipe now and stop writing until the next Search. What a Clear button calls. */
  clear: () => void;
  /** Force a write now (e.g. just before an imperative navigate). Honours the gates. */
  flush: () => void;
}

function storageKey(routeKey: string): string {
  return RETAINED_SEARCH_PREFIX + routeKey;
}

/** `sessionStorage` throws outright in some privacy modes — probe once, per call, in a try. */
function store(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * True when this context may retain anything at all: a real `sessionStorage`, not the login
 * route. (The kiosk half needs the session and lives in the hook.)
 */
export function retentionAvailable(): boolean {
  if (store() === null) return false;
  try {
    const path = window.location.pathname;
    if (path === '/login' || path.startsWith('/login/')) return false;
  } catch {
    return false;
  }
  return true;
}

/** Read an entry, dropping (and deleting) one that has been idle past its TTL. */
export function readRetainedSearch<S, R>(
  routeKey: string,
  ttlMs: number = RETAINED_SEARCH_TTL_MS,
): RetainedSearch<S, R> | null {
  if (!retentionAvailable()) return null;
  const s = store();
  if (!s) return null;
  try {
    const raw = s.getItem(storageKey(routeKey));
    if (raw == null) return null;
    const parsed = JSON.parse(raw) as Partial<RetainedSearch<S, R>>;
    if (!parsed || typeof parsed !== 'object' || typeof parsed.updatedAt !== 'number') {
      s.removeItem(storageKey(routeKey));
      return null;
    }
    if (Date.now() - parsed.updatedAt > ttlMs) {
      // Expired by inactivity. Delete rather than leave it to rot — an operator who comes back
      // after lunch gets the pristine screen PB would have given them after a re-open.
      s.removeItem(storageKey(routeKey));
      return null;
    }
    return {
      state: parsed.state as S,
      results: (parsed.results ?? null) as R | null,
      resultsDropped: parsed.resultsDropped === true,
      view: parsed.view ?? {},
      updatedAt: parsed.updatedAt,
    };
  } catch {
    // Corrupt JSON (a half-written entry from a killed tab) must never break a screen.
    try {
      s?.removeItem(storageKey(routeKey));
    } catch {
      /* nothing else to do */
    }
    return null;
  }
}

/**
 * One-entry memo of the last serialised result set, keyed by object IDENTITY.
 *
 * <p>TanStack hands back the same `data` object until a refetch replaces it, and a page holding
 * rows in `useState` sets them once per search — so every write that follows a criteria edit
 * (a Query By keystroke, a criteria-pane toggle) carries the SAME rows. Without this, each of
 * those would re-walk a result set that can be megabytes. Identity comparison only: a different
 * object is always re-serialised, so a stale copy is impossible.
 */
let resultsSerialCache: { rows: unknown; json: string | null } | null = null;

function serialiseResults(rows: unknown): string | null {
  if (resultsSerialCache !== null && resultsSerialCache.rows === rows) {
    return resultsSerialCache.json;
  }
  let json: string | null;
  try {
    json = JSON.stringify(rows) ?? null;
  } catch {
    json = null; // circular / non-serialisable rows: treated as "too big", criteria still kept
  }
  resultsSerialCache = { rows, json };
  return json;
}

/**
 * Write an entry, applying the size guard. Returns what was actually stored (rows dropped or
 * not), or null when nothing could be written.
 */
export function writeRetainedSearch<S, R>(
  routeKey: string,
  entry: { state: S; results?: R | null | undefined; view?: RetainedView | null | undefined },
): RetainedSearch<S, R> | null {
  if (!retentionAvailable()) return null;
  const s = store();
  if (!s) return null;

  const rows = (entry.results ?? null) as R | null;
  let serialisedRows = rows === null ? null : serialiseResults(rows);
  let results = rows;
  let resultsDropped = false;
  if (rows !== null
    && (serialisedRows === null || serialisedRows.length > RETAINED_SEARCH_MAX_RESULT_BYTES)) {
    results = null;
    serialisedRows = null;
    resultsDropped = true;
  }

  const payload: RetainedSearch<S, R> = {
    state: entry.state,
    results,
    resultsDropped,
    view: entry.view ?? {},
    updatedAt: Date.now(),
  };

  // Assembled by hand from the already-serialised rows rather than `JSON.stringify(payload)`,
  // which would walk a multi-megabyte result set a SECOND time on every write.
  const body =
    `{"state":${JSON.stringify(payload.state)}`
    + `,"results":${serialisedRows ?? 'null'}`
    + `,"resultsDropped":${resultsDropped}`
    + `,"view":${JSON.stringify(payload.view)}`
    + `,"updatedAt":${payload.updatedAt}}`;

  try {
    s.setItem(storageKey(routeKey), body);
    return payload;
  } catch {
    // Quota exceeded (another route's rows filled the tab). Retry criteria-only: the operator
    // gets their typing back and the page re-retrieves, which is the whole point of the flag.
    const lean: RetainedSearch<S, R> = { ...payload, results: null, resultsDropped: true };
    try {
      s.setItem(storageKey(routeKey), JSON.stringify(lean));
      return lean;
    } catch {
      return null;
    }
  }
}

/** Delete one entry. */
export function clearRetainedSearch(routeKey: string): void {
  const s = store();
  if (!s) return;
  try {
    s.removeItem(storageKey(routeKey));
  } catch {
    /* ignore */
  }
}

/** Delete every entry in this tab. Sign-out, and the quota escape hatch. */
export function clearAllRetainedSearches(): void {
  const s = store();
  if (!s) return;
  try {
    const doomed: string[] = [];
    for (let i = 0; i < s.length; i += 1) {
      const k = s.key(i);
      if (k && k.startsWith(RETAINED_SEARCH_PREFIX)) doomed.push(k);
    }
    doomed.forEach((k) => s.removeItem(k));
  } catch {
    /* ignore */
  }
}

/**
 * slice-3952 (#3780/#3785/#3799/#3800/#3803/#3823): a record was created / changed / deleted
 * somewhere in this tab, so every retained RESULT SET may now be wrong. Drop the rows (criteria
 * and view are kept) and flag the entry {@link RetainedSearch.resultsDropped}, which every
 * consumer already reads as "re-run the search on return" — the PB re-retrieve. Called from the
 * data-write signal (`shared/api/dataWrites`), never on a plain navigation.
 */
export function dropAllRetainedResults(): void {
  const s = store();
  if (!s) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < s.length; i += 1) {
      const k = s.key(i);
      if (k && k.startsWith(RETAINED_SEARCH_PREFIX)) keys.push(k);
    }
    for (const k of keys) {
      const raw = s.getItem(k);
      if (raw == null || raw.includes('"results":null')) continue;
      try {
        const parsed = JSON.parse(raw) as Partial<RetainedSearch<unknown, unknown>>;
        if (parsed?.results == null) continue;
        s.setItem(k, JSON.stringify({ ...parsed, results: null, resultsDropped: true }));
      } catch {
        s.removeItem(k);
      }
    }
  } catch {
    /* storage unavailable — nothing retained to go stale */
  }
}

/**
 * The default route key for a dedicated page: its full path + query string.
 *
 * <p>The path segments matter — `/items/inquiry/ABC` and `/items/inquiry/XYZ` are two different
 * screens' worth of criteria — and so does the query string, for the same reason the generic
 * browse remounts on it. Pass a `suffix` for anything else that partitions the screen (a tab id,
 * a mode).
 *
 * <p><b>`includeSearch: false`</b> (slice-3580) is for the pages whose query string is a VIEW
 * selector rather than a different screen — the v2 rail writes `?view=all` / `?view=criteria`
 * into the URL as the operator moves around ONE search. Keying on that would split a single
 * search across several entries and hand the operator back an empty pane whenever the view
 * they returned to was not the view they searched in.
 */
export function useRetainedRouteKey(suffix = '', includeSearch = true): string {
  const { pathname, search } = useLocation();
  return useMemo(
    () => `${pathname}${includeSearch ? search : ''}${suffix}`,
    [pathname, search, suffix, includeSearch],
  );
}

/**
 * Retain one screen's search across a zoom + Back. See the file header for the why.
 *
 * <p>Usage is two halves, both required:
 *
 * <ol>
 *   <li><b>Restore</b> — seed the page's own `useState` initialisers from `restored.state`
 *       (a lazy initialiser, so it happens before the first paint and the query starts with the
 *       right key), and re-run the search when {@link UseRetainedSearchResult.resultsDropped}.</li>
 *   <li><b>Retain</b> — pass the committed criteria as {@link UseRetainedSearchOptions.state}
 *       and gate it with {@link UseRetainedSearchOptions.retain}.</li>
 * </ol>
 */
export function useRetainedSearch<S, R = unknown>({
  routeKey,
  state,
  results,
  getView,
  retain = true,
  searchToken,
  clearToken,
  enabled = true,
  ttlMs = RETAINED_SEARCH_TTL_MS,
  initialEntry,
}: UseRetainedSearchOptions<S, R>): UseRetainedSearchResult<S, R> {
  // A kiosk display is a shared screen — see the file header.
  const kiosk = useAuthStore((s) => s.session?.kiosk === true);
  const active = enabled && !kiosk && routeKey !== '';

  // Read ONCE. A lazy initialiser (not an effect) so a caller's own lazy initialiser, running in
  // the same first render, can consume it.
  const [restored] = useState<RetainedSearch<S, R> | null>(() => {
    if (!active) return null;
    if (initialEntry !== undefined) return initialEntry;
    return readRetainedSearch<S, R>(routeKey, ttlMs);
  });

  // Clear suppresses writing until the next Search, so the re-render that follows a Clear (new
  // criteria, results still on screen) cannot immediately rewrite the entry we just deleted.
  const suppressedRef = useRef(false);
  const clearSeenRef = useRef<string | number | null | undefined>(clearToken);
  const searchSeenRef = useRef<string | number | null | undefined>(searchToken);

  if (clearToken !== clearSeenRef.current) {
    clearSeenRef.current = clearToken;
    suppressedRef.current = true;
    clearRetainedSearch(routeKey);
  }
  if (searchToken !== searchSeenRef.current) {
    searchSeenRef.current = searchToken;
    suppressedRef.current = false;
  }

  // Latest values for the unmount write, which must not re-subscribe on every render.
  const latest = useRef({ routeKey, state, results, getView, retain, active });
  latest.current = { routeKey, state, results, getView, retain, active };

  const doWrite = useCallback(() => {
    const l = latest.current;
    if (!l.active || !l.retain || suppressedRef.current) return;
    writeRetainedSearch<S, R>(l.routeKey, {
      state: l.state,
      results: l.results ?? null,
      view: l.getView?.() ?? null,
    });
  }, []);

  // Write on every committed-state change, debounced — see RETAINED_SEARCH_WRITE_DEBOUNCE_MS.
  // `results` is compared by reference: TanStack hands back the same object until a refetch
  // replaces it, and a page holding rows in `useState` sets them once per search.
  useEffect(() => {
    const t = setTimeout(doWrite, RETAINED_SEARCH_WRITE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [doWrite, routeKey, state, results, retain, active]);

  // The write that matters for scroll position: the operator drilling away unmounts this page,
  // and `getView()` is sampled at that moment. `pagehide` covers a tab close / hard reload.
  useEffect(() => {
    const onHide = () => doWrite();
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      doWrite();
    };
  }, [doWrite]);

  const clear = useCallback(() => {
    suppressedRef.current = true;
    clearRetainedSearch(latest.current.routeKey);
  }, []);

  return {
    restored,
    resultsDropped: restored?.resultsDropped === true,
    clear,
    flush: doWrite,
  };
}
